/**
 * cart-persist.js — canonical "persist the multi-address cart" routine for the
 * Spicers theme, shared by cart.js and global.js (QuantityInput).
 *
 * Environment notes:
 *  - Served as a plain `<script defer>` asset (no bundler), loaded BEFORE both
 *    global.js and cart.js so `window.SpicersCart` exists before either runs.
 *  - No jQuery dependency: reads the DOM with querySelector and talks to the
 *    Shopify /cart AJAX API with fetch. (This replaces the old jQuery `$.ajax`
 *    implementation that lived in global.js.)
 *  - Reads the per-line-item fields rendered in sections/main-cart-items.liquid:
 *      .jq-address-dropdown / .jq-address-{n}  → <select>, value is the
 *                                                comma-joined address string,
 *                                                data-variant-id on the element
 *      .jq-due-date-{n}                        → <input>, "Delivery Date" = unset
 *      #Quantity-{n}                           → <input>, line quantity
 *      .add-new-giftMessage-{n}                → <input>, "Add Gift Message" = unset
 *
 * Exposes a single global: window.SpicersCart.persistCart().
 */
(function () {
  "use strict";

  // CART_ATTRIBUTES lives in cart-utils.js (loaded first) as a shared global.
  // Placeholder values rendered into the inputs when the user hasn't picked
  // anything yet — treated as "empty".
  const DUE_DATE_PLACEHOLDER = "Delivery Date";
  const GIFT_MESSAGE_PLACEHOLDER = "Add Gift Message";

  // postCartJSON() lives in cart-utils.js (loaded first) as a shared global.

  // Parse the comma-joined address string stored as a <select> option value
  // back into its fields. Country is always forced to "GB" (existing behaviour).
  function parseAddressString(value) {
    const parts = (value || "").split(",");
    return {
      firstName: parts[0]?.trim(),
      lastName: parts[1]?.trim(),
      email: parts[2]?.trim(),
      contactNumber: parts[3]?.trim(),
      postcode: parts[4]?.trim(),
      companyName: parts[5]?.trim(),
      address: parts[6]?.trim(),
      addressDetail: parts[7]?.trim(),
      city: parts[8]?.trim(),
      country: "GB",
    };
  }

  function extractGiftMessage(itemIndex) {
    const message = document.querySelector(`.add-new-giftMessage-${itemIndex}`)?.value;
    return message === GIFT_MESSAGE_PLACEHOLDER ? "" : message || "";
  }

  function uuid() {
    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
      (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16),
    );
  }

  // The line-item property shape used for every cart line, with all address
  // fields blank — for placeholder lines that carry no consignee address yet.
  function emptyLineItemProperties() {
    return {
      "first name": "",
      "last name": "",
      "consignee email": "",
      phone: "",
      zip: "",
      company: "",
      address1: "",
      address2: "",
      city: "",
      country: "",
      "due date": "",
      "gift message": "",
      _rand: uuid(),
    };
  }

  // Clear the cart, then rebuild it from the per-line-item address dropdowns so
  // every line carries its address / due-date / gift-message line-item
  // properties. Items are re-added in reverse so the original cart order is
  // preserved.
  async function persistCart() {
    await postCartJSON("/cart/clear.js");

    const items = [];

    // When a delivery option other than "Single Address Delivery" is selected
    // (signalled by the .delivery-collect-selected body class toggled in the
    // delivery-options section), every line's address / due-date / gift-message
    // properties are forced to an empty string.
    const collectSelected = document.body.classList.contains("delivery-collect-selected");

    document.querySelectorAll(".jq-address-dropdown").forEach((dropdown, index) => {
      const itemIndex = index + 1;
      const addr = parseAddressString(dropdown.value);
      const hasAddress = Boolean(addr.firstName?.trim());

      const dueDateField = document.querySelector(`.jq-due-date-${itemIndex}`)?.value;
      const dueDate = dueDateField !== DUE_DATE_PLACEHOLDER ? dueDateField || "" : "";
      const quantity = document.querySelector(`#Quantity-${itemIndex}`)?.value;

      items.push({
        id: dropdown.dataset.variantId,
        quantity,
        properties: {
          "first name": hasAddress ? addr.firstName : "",
          "last name": hasAddress ? addr.lastName : "",
          "consignee email": hasAddress ? addr.email : "",
          phone: hasAddress ? addr.contactNumber : "",
          zip: collectSelected ? "" : (hasAddress ? addr.postcode : ""),
          company: collectSelected ? "" : (hasAddress ? addr.companyName : ""),
          address1: collectSelected ? "" : (hasAddress ? addr.address : ""),
          address2: collectSelected ? "" : (hasAddress ? addr.addressDetail : ""),
          city: collectSelected ? "" : (hasAddress ? addr.city : ""),
          country: collectSelected ? "" : (hasAddress ? addr.country : ""),
          "due date": collectSelected ? "" : dueDate,
          "gift message": collectSelected ? "" : extractGiftMessage(itemIndex),
          _rand: uuid(),
        },
      });
    });

    await postCartJSON("/cart/add.js", {
      items: items.reverse(),
      attributes: CART_ATTRIBUTES,
    });
  }

  window.SpicersCart = window.SpicersCart || {};
  Object.assign(window.SpicersCart, {
    persistCart,
    parseAddressString,
    extractGiftMessage,
    emptyLineItemProperties,
    uuid,
    GIFT_MESSAGE_PLACEHOLDER,
  });
})();